/**
 * 頁面權限配置
 *
 * 定義所有可控制的頁面權限及其所屬模式
 * 格式: {module}:{page}，如 management:AIPromptManagement
 *
 * NOTE: 頁面權限現在從統一配置系統 (remotes/index.ts) 自動生成
 * 新增頁面請在 remotes/*.ts 中配置
 */

// ============================================================================
// Import from Unified Config System
// ============================================================================

import {
    generatedPagePermissions,
    getDefaultPermissions as getDefaultPermsFromRemotes,
    localModes as localModesFromRemotes,
    type NavMode,
    type PagePermissionConfig,
} from './remotes';

// ============================================================================
// Re-export Types
// ============================================================================

export type { NavMode, PagePermissionConfig };

// ============================================================================
// Constants
// ============================================================================

/**
 * 本地模式（不需要權限控制，依賴案場權限）
 * 這些模式下的頁面不受頁面權限系統控制
 *
 * NOTE: 從統一配置系統導入
 */
export const localModes: NavMode[] = localModesFromRemotes;

/**
 * 導航模式完整配置（統一定義）
 * 用於 SideNavDrawer、權限管理頁面等
 */
export interface NavModeConfig {
    key: NavMode;
    title: string;
    icon: string;
    /** 僅視覺效果（看起來像禁用但可點擊） */
    visualOnly?: boolean;
}

export const navModeConfigs: NavModeConfig[] = [
    { key: 'warroom', title: '戰情', icon: 'dashboard' },
    { key: 'operation', title: '維運', icon: 'wrench' },
    { key: 'bigscreen', title: '大屏', icon: 'screen' },
    { key: 'tools', title: '工具', icon: 'wand' },
    { key: 'report', title: '報表', icon: 'chart' },
    { key: 'org', title: '組織', icon: 'groups' },
    { key: 'quotation', title: '業務', icon: 'briefcase' },
    { key: 'hr', title: '人資', icon: 'users' },
    { key: 'workflow', title: '流程', icon: 'workflows' },
    { key: 'ess', title: '儲能', icon: 'lamp' },
    { key: 'files', title: '檔案', icon: 'folder-open' },
    { key: 'chat', title: '通訊', icon: 'messaging' },
    { key: 'management', title: '管理', icon: 'shield' },
    { key: 'test', title: '--', icon: 'cube', visualOnly: true },
    { key: 'placeholder', title: '--', icon: 'cube', visualOnly: true },
];

/** 模式順序（從 navModeConfigs 導出） */
export const modeOrder: NavMode[] = navModeConfigs.map(m => m.key);

/**
 * 完整頁面權限配置
 * key 格式: {module}:{page}
 *
 * NOTE: 自動從 remotes/*.ts 生成
 * 新增頁面請在對應的 remotes/*.ts 中配置
 */
export const pagePermissions: Record<string, PagePermissionConfig> = generatedPagePermissions;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * 取得預設權限列表
 * 用於新組織建立時自動授予的權限
 *
 * NOTE: 使用統一配置系統的實作
 */
export function getDefaultPermissions(): string[] {
    return getDefaultPermsFromRemotes();
}

/**
 * 從頁面權限推導可用的模式
 * @param permissions 用戶擁有的頁面權限列表
 * @returns 可存取的模式列表
 */
export function deriveModesFromPermissions(permissions: string[]): NavMode[] {
    const modes = new Set<NavMode>();

    // 本地模式永遠可用
    localModes.forEach(mode => modes.add(mode));

    // 從頁面權限推導模式
    const unmatchedPerms: string[] = [];
    permissions.forEach(perm => {
        const config = pagePermissions[perm];
        if (config) {
            modes.add(config.mode);
        } else {
            unmatchedPerms.push(perm);
        }
    });

    // Debug: 輸出推導結果
    console.log('[deriveModesFromPermissions]', {
        inputPermissions: permissions,
        derivedModes: Array.from(modes),
        unmatchedPermissions: unmatchedPerms.length > 0 ? unmatchedPerms : '(none)'
    });

    return Array.from(modes);
}

/**
 * 檢查頁面權限
 * @param permissions 用戶擁有的頁面權限列表
 * @param dir 模組目錄（如 om, management）
 * @param page 頁面名稱
 * @returns 是否有權限
 */
export function hasPagePermission(permissions: string[], dir: string, page: string): boolean {
    const permKey = `${dir}:${page}`;

    // 本地頁面不需要權限
    if (localModes.includes(dir as NavMode)) {
        return true;
    }

    // localhost 開發模式：所有頁面跳過權限
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        return true;
    }

    return permissions.includes(permKey);
}

/**
 * 取得特定模式下的所有頁面權限
 * @param mode 導航模式
 * @returns 該模式下的所有頁面權限鍵值
 */
export function getPermissionsByMode(mode: NavMode): string[] {
    return Object.entries(pagePermissions)
        .filter(([_, config]) => config.mode === mode)
        .map(([key]) => key);
}

/**
 * 從 SideNav 路徑解析出權限鍵
 * @param path SideNav 菜單項目的路徑，如 '/om/FocusField' 或 '/tools/UserUIList'
 * @returns 權限鍵，如 'om:FocusField'，若為本地頁面則返回 null
 */
export function parsePermissionKeyFromPath(path: string): string | null {
    // 防禦性檢查：path 為空或 undefined 時返回 null
    if (!path) {
        return null;
    }

    // 統一路徑格式: '/dir/page'
    if (path.startsWith('/')) {
        const parts = path.replace('/', '').split('/');
        if (parts.length >= 2) {
            const dir = parts[0];
            const page = parts[1].split('?')[0]; // 移除 query string
            // 本地頁面不需要權限控制
            if (localModes.includes(dir as NavMode)) {
                return null;
            }
            // UserUIRenderer 不需要獨立權限（由 UserUIList 進入）
            if (dir === 'tools' && page === 'UserUIRenderer') {
                return null;
            }
            // localhost 開發模式：所有頁面跳過權限
            if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
                return null;
            }
            return `${dir}:${page}`;
        }
    }

    return null;
}

/**
 * 模式與中文名稱對照
 */
export const modeLabels: Record<NavMode, string> = {
    operation: '維運',
    warroom: '戰情',
    bigscreen: '大屏',
    chat: '通訊',
    report: '報表',
    management: '管理',
    tools: '工具',
    workflow: '流程',
    quotation: '業務',
    hr: '人資',
    data: '數據',
    ess: '儲能',
    org: '組織',
    files: '檔案',
    test: '--',
    placeholder: '--',
    field: '案場',
};

export default {
    pagePermissions,
    localModes,
    navModeConfigs,
    modeOrder,
    getDefaultPermissions,
    deriveModesFromPermissions,
    hasPagePermission,
    getPermissionsByMode,
    parsePermissionKeyFromPath,
    modeLabels,
};
